// src/components/RhinoVOIPReports.jsx
import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  Icon,
  UtilityInlineGrid,
} from 'rhinostyle';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useGetCdrsQuery, useGetMembersWithExtensionsQuery } from '../services/VoipService';
import PageLoader from './PageLoader';
import EmptyMessage from './EmptyMessage';

const PAGE_SIZE = 1000;

const DATE_PICKER_LABELS = [
  { id: 1, label: 'Yesterday', duration: 1 },
  { id: 2, label: 'Last 7 Days', duration: 6 },
  { id: 3, label: 'Last 30 Days', duration: 29 },
  { id: 4, label: 'Last 90 Days', duration: 89 },
  { id: 5, label: 'Last 12 Months', duration: 'year' },
  { id: 6, label: 'Custom Date' },
];

const PIE_COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4'];

const REPORT_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'members', label: 'Members' },
];

const MEMBER_CHART_COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4', '#795548', '#607d8b', '#f44336', '#3f51b5'];

function downloadCsv(columns, rows, filename) {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const header = columns.map((c) => escape(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => escape(typeof c.format === 'function' ? c.format(row) : row[c.key])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const RhinoVOIPReports = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(3);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRingGroup, setSelectedRingGroup] = useState(null);
  const [sortColumn, setSortColumn] = useState('total');
  const [sortAsc, setSortAsc] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());
  const [hiddenDirections, setHiddenDirections] = useState(new Set());
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [hiddenLineMembers, setHiddenLineMembers] = useState(new Set());
  const [summaryGrouping, setSummaryGrouping] = useState('total');
  const [summarySortColumn, setSummarySortColumn] = useState('period');
  const [summarySortAsc, setSummarySortAsc] = useState(true);
  const [memberGrouping, setMemberGrouping] = useState('total');
  const [viewMode, setViewMode] = useState('graphs');

  const { data: membersData } = useGetMembersWithExtensionsQuery();
  const members = Array.isArray(membersData?.objects) ? membersData.objects : [];

  const filters = useMemo(() => ({
    start_stamp: startDate.format('YYYY-MM-DD'),
    end_stamp: endDate.format('YYYY-MM-DD'),
    limit: PAGE_SIZE,
    offset: 0,
  }), [startDate, endDate]);

  const { data, isLoading, isFetching } = useGetCdrsQuery(filters);
  const allRecords = Array.isArray(data?.objects) ? data.objects : [];
  const records = useMemo(() => {
    let filtered = allRecords;
    if (selectedMember) {
      filtered = filtered.filter((r) =>
        r.caller_id_number?.includes(selectedMember) ||
        r.destination_number?.includes(selectedMember));
    }
    if (selectedRingGroup) {
      filtered = filtered.filter((r) => r.ring_group_name === selectedRingGroup);
    }
    return filtered;
  }, [allRecords, selectedMember, selectedRingGroup]);

  const memberLabel = useMemo(() => {
    if (!selectedMember) return 'All Members';
    const m = members.find((mb) => mb.extension === selectedMember);
    return m ? `${m.firstName} ${m.lastName}` : selectedMember;
  }, [selectedMember, members]);

  const ringGroupOptions = useMemo(() => {
    const seen = new Set();
    return allRecords.reduce((acc, r) => {
      const name = r.ring_group_name;
      if (name && !seen.has(name)) {
        seen.add(name);
        acc.push({ id: name, value: name });
      }
      return acc;
    }, []).sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  const ringGroupLabel = selectedRingGroup || 'All Ring Groups';

  const isAnyFilterSelected = !!selectedMember || !!selectedRingGroup;

  const clearAllFilters = () => {
    setSelectedMember(null);
    setSelectedRingGroup(null);
  };

  // --- Date handlers ---

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      setStartDate(moment(date.startDate));
      setEndDate(moment(date.endDate));
      setActiveKey(date.activeKey);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();
    if (selectedLabelIndex === 1) {
      toDate = moment().subtract(1, 'days');
    }
    if (duration === 'year') {
      fromDate = moment().subtract(1, 'years').add(1, 'days');
    }
    return { startDate: fromDate, endDate: toDate };
  };

  // --- Chart data computations ---

  // Format a UTC timestamp to MM/DD using UTC date (matches API filtering)
  function utcDay(stamp) {
    const d = new Date(stamp);
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${m}/${day}`;
  }

  const totalDaysInRange = useMemo(() => {
    const diffDays = endDate.diff(startDate, 'days') + 1;
    return Math.max(diffDays, 1);
  }, [startDate, endDate]);

  const callsByHour = useMemo(() => {
    const hourCounts = Array(24).fill(0);

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const d = new Date(r.start_stamp);
      hourCounts[d.getUTCHours()] += 1;
    });

    return hourCounts.map((count, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      avgCalls: Math.round((count / totalDaysInRange) * 10) / 10,
    }));
  }, [records, totalDaysInRange]);

  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const avgCallsByDayOfWeek = useMemo(() => {
    // Count how many of each weekday exist in the selected date range
    const dowCountsInRange = Array(7).fill(0);
    const cursor = startDate.clone().startOf('day');
    const end = endDate.clone().startOf('day');
    while (cursor.isSameOrBefore(end)) {
      // moment day(): 0=Sun, convert to 0=Mon
      const dow = (cursor.day() + 6) % 7;
      dowCountsInRange[dow] += 1;
      cursor.add(1, 'day');
    }

    const dayCounts = Array(7).fill(0);
    records.forEach((r) => {
      if (!r.start_stamp) return;
      const d = new Date(r.start_stamp);
      const dow = (d.getUTCDay() + 6) % 7;
      dayCounts[dow] += 1;
    });

    return DAY_NAMES.map((name, i) => ({
      day: name,
      avgCalls: dowCountsInRange[i] > 0 ? Math.round((dayCounts[i] / dowCountsInRange[i]) * 10) / 10 : 0,
    }));
  }, [records, startDate, endDate]);

  const callsPerDay = useMemo(() => {
    const dayCounts = {};

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const day = utcDay(r.start_stamp);
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    });

    return Object.entries(dayCounts)
      .map(([date, count]) => ({ date, calls: count }))
      .sort((a, b) => {
        const [am, ad] = a.date.split('/').map(Number);
        const [bm, bd] = b.date.split('/').map(Number);
        return am - bm || ad - bd;
      });
  }, [records]);

  const callsByDirectionPerDay = useMemo(() => {
    const dayCounts = {};

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const day = utcDay(r.start_stamp);
      if (!dayCounts[day]) dayCounts[day] = { inbound: 0, outbound: 0 };
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') dayCounts[day].inbound += 1;
      else if (dir === 'outbound') dayCounts[day].outbound += 1;
    });

    return Object.entries(dayCounts)
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => {
        const [am, ad] = a.date.split('/').map(Number);
        const [bm, bd] = b.date.split('/').map(Number);
        return am - bm || ad - bd;
      });
  }, [records]);

  const callsByStatus = useMemo(() => {
    const counts = {};
    records.forEach((r) => {
      const status = r.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  const callsByDirection = useMemo(() => {
    const counts = {};
    records.forEach((r) => {
      const dir = r.direction || 'unknown';
      counts[dir] = (counts[dir] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  // --- Summary table data ---

  function utcWeek(stamp) {
    const d = moment.utc(stamp);
    return `${d.startOf('isoWeek').format('MM/DD')} - ${d.endOf('isoWeek').format('MM/DD')}`;
  }

  function utcMonth(stamp) {
    const d = new Date(stamp);
    return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
  }

  const summaryTableData = useMemo(() => {
    const buckets = {};

    const getBucketKey = (r) => {
      if (summaryGrouping === 'total') return 'Total';
      if (summaryGrouping === 'day') return utcDay(r.start_stamp);
      if (summaryGrouping === 'week') return utcWeek(r.start_stamp);
      if (summaryGrouping === 'month') return utcMonth(r.start_stamp);
      return 'Total';
    };

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const key = getBucketKey(r);
      if (!buckets[key]) {
        buckets[key] = { total: 0, inbound: 0, outbound: 0, missed: 0, totalDuration: 0, answeredCount: 0 };
      }
      const b = buckets[key];
      b.total += 1;
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') b.inbound += 1;
      else if (dir === 'outbound') b.outbound += 1;
      const isMissed = r.status === 'missed' || r.status === 'no_answer';
      if (isMissed) b.missed += 1;
      const duration = r.billsec || r.duration || 0;
      if (!isMissed && duration > 0) {
        b.totalDuration += duration;
        b.answeredCount += 1;
      }
    });

    const daysPerBucket = (key) => {
      if (summaryGrouping === 'total') return totalDaysInRange;
      if (summaryGrouping === 'day') return 1;
      if (summaryGrouping === 'week') return 7;
      if (summaryGrouping === 'month') {
        // parse MM/YYYY
        const [mm, yyyy] = key.split('/').map(Number);
        return new Date(yyyy, mm, 0).getDate();
      }
      return 1;
    };

    return Object.entries(buckets).map(([period, b]) => ({
      period,
      total: b.total,
      avgPerDay: Math.round((b.total / daysPerBucket(period)) * 10) / 10,
      inbound: b.inbound,
      outbound: b.outbound,
      missed: b.missed,
      pctMissed: b.total > 0 ? Math.round((b.missed / b.total) * 100) : 0,
      avgHandleTime: b.answeredCount > 0 ? Math.round(b.totalDuration / b.answeredCount) : 0,
    }));
  }, [records, summaryGrouping, totalDaysInRange]);

  const handleSummarySort = (col) => {
    if (summarySortColumn === col) {
      setSummarySortAsc((prev) => !prev);
    } else {
      setSummarySortColumn(col);
      setSummarySortAsc(col === 'period');
    }
  };

  const sortedSummaryTableData = useMemo(() => {
    const sorted = [...summaryTableData].sort((a, b) => {
      const aVal = a[summarySortColumn];
      const bVal = b[summarySortColumn];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal);
      return aVal - bVal;
    });
    return summarySortAsc ? sorted : sorted.reverse();
  }, [summaryTableData, summarySortColumn, summarySortAsc]);

  // --- Members tab data ---

  const memberStats = useMemo(() => {
    // Seed with all known members
    const stats = {};
    members.forEach((m) => {
      stats[m.extension] = {
        extension: m.extension,
        name: `${m.firstName} ${m.lastName}`,
        total: 0,
        inbound: 0,
        outbound: 0,
        missed: 0,
        totalDuration: 0,
        answeredCount: 0,
      };
    });

    records.forEach((r) => {
      const callerExt = r.caller_extension;
      const callerName = r.caller_extension_name || r.caller_id_name;
      const destExt = r.dest_extension;
      const destName = r.dest_name;
      const dir = (r.direction || '').toLowerCase();
      const isMissed = r.status === 'missed' || r.status === 'no_answer';
      const duration = r.billsec || r.duration || 0;

      const touch = (ext, name) => {
        if (!stats[ext]) {
          stats[ext] = { extension: ext, name: name || ext, total: 0, inbound: 0, outbound: 0, missed: 0, totalDuration: 0, answeredCount: 0 };
        }
        stats[ext].total += 1;
        if (dir === 'inbound') stats[ext].inbound += 1;
        else if (dir === 'outbound') stats[ext].outbound += 1;
        if (isMissed) stats[ext].missed += 1;
        if (!isMissed && duration > 0) {
          stats[ext].totalDuration += duration;
          stats[ext].answeredCount += 1;
        }
      };

      if (callerExt) touch(callerExt, callerName);
      if (destExt && destExt !== callerExt) touch(destExt, destName);
    });

    return Object.values(stats)
      .map((m) => ({
        name: m.name,
        extension: m.extension,
        total: m.total,
        avgPerDay: Math.round((m.total / totalDaysInRange) * 10) / 10,
        inbound: m.inbound,
        outbound: m.outbound,
        missed: m.missed,
        pctMissed: m.total > 0 ? Math.round((m.missed / m.total) * 100) : 0,
        avgHandleTime: m.answeredCount > 0 ? Math.round(m.totalDuration / m.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, members, totalDaysInRange]);

  // Keep a chart-friendly alias
  const callsPerMember = memberStats;

  const memberTotalsPieData = useMemo(() =>
    memberStats.filter((m) => m.total > 0).map((m) => ({ name: m.name, value: m.total })),
  [memberStats]);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortAsc((prev) => !prev);
    } else {
      setSortColumn(col);
      setSortAsc(col === 'name' || col === 'extension');
    }
  };

  const memberStatsByPeriod = useMemo(() => {
    if (memberGrouping === 'total') return memberStats;

    const buckets = {};
    const getBucketKey = (r) => {
      if (memberGrouping === 'day') return utcDay(r.start_stamp);
      if (memberGrouping === 'week') return utcWeek(r.start_stamp);
      if (memberGrouping === 'month') return utcMonth(r.start_stamp);
      return 'Total';
    };

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const period = getBucketKey(r);

      const callerExt = r.caller_extension;
      const callerName = r.caller_extension_name || r.caller_id_name;
      const destExt = r.dest_extension;
      const destName = r.dest_name;
      const dir = (r.direction || '').toLowerCase();
      const isMissed = r.status === 'missed' || r.status === 'no_answer';
      const duration = r.billsec || r.duration || 0;

      const touch = (ext, name) => {
        const key = `${ext}|${period}`;
        if (!buckets[key]) {
          buckets[key] = { extension: ext, name: name || ext, period, total: 0, inbound: 0, outbound: 0, missed: 0, totalDuration: 0, answeredCount: 0 };
        }
        buckets[key].total += 1;
        if (dir === 'inbound') buckets[key].inbound += 1;
        else if (dir === 'outbound') buckets[key].outbound += 1;
        if (isMissed) buckets[key].missed += 1;
        if (!isMissed && duration > 0) {
          buckets[key].totalDuration += duration;
          buckets[key].answeredCount += 1;
        }
      };

      if (callerExt) touch(callerExt, callerName);
      if (destExt && destExt !== callerExt) touch(destExt, destName);
    });

    const daysPerBucket = (period) => {
      if (memberGrouping === 'day') return 1;
      if (memberGrouping === 'week') return 7;
      if (memberGrouping === 'month') {
        const [mm, yyyy] = period.split('/').map(Number);
        return new Date(yyyy, mm, 0).getDate();
      }
      return 1;
    };

    return Object.values(buckets)
      .map((b) => ({
        name: b.name,
        extension: b.extension,
        period: b.period,
        total: b.total,
        avgPerDay: Math.round((b.total / daysPerBucket(b.period)) * 10) / 10,
        inbound: b.inbound,
        outbound: b.outbound,
        missed: b.missed,
        pctMissed: b.total > 0 ? Math.round((b.missed / b.total) * 100) : 0,
        avgHandleTime: b.answeredCount > 0 ? Math.round(b.totalDuration / b.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, memberStats, memberGrouping]);

  const sortedMemberStats = useMemo(() => {
    const sorted = [...memberStatsByPeriod].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal);
      return aVal - bVal;
    });
    return sortAsc ? sorted : sorted.reverse();
  }, [memberStatsByPeriod, sortColumn, sortAsc]);

  const memberCallsPerDay = useMemo(() => {
    // Build a map: { date -> { memberKey -> count } }
    const dayMap = {};
    const memberKeys = new Set();

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const day = utcDay(r.start_stamp);
      if (!dayMap[day]) dayMap[day] = {};

      const callerExt = r.caller_extension;
      const destExt = r.dest_extension;

      if (callerExt) {
        const key = callerExt;
        memberKeys.add(key);
        dayMap[day][key] = (dayMap[day][key] || 0) + 1;
      }
      if (destExt && destExt !== callerExt) {
        const key = destExt;
        memberKeys.add(key);
        dayMap[day][key] = (dayMap[day][key] || 0) + 1;
      }
    });

    return {
      data: Object.entries(dayMap)
        .map(([date, counts]) => ({ date, ...counts }))
        .sort((a, b) => {
          const [am, ad] = a.date.split('/').map(Number);
          const [bm, bd] = b.date.split('/').map(Number);
          return am - bm || ad - bd;
        }),
      memberKeys: [...memberKeys],
    };
  }, [records]);

  // --- Helpers ---

  function formatHandleTime(seconds) {
    if (!seconds) return '00:00';
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  }

  const toggleStatus = useCallback((entry) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const toggleDirection = useCallback((entry) => {
    setHiddenDirections((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const filteredCallsByStatus = useMemo(() =>
    callsByStatus.filter((d) => !hiddenStatuses.has(d.name)),
  [callsByStatus, hiddenStatuses]);

  const filteredCallsByDirection = useMemo(() =>
    callsByDirection.filter((d) => !hiddenDirections.has(d.name)),
  [callsByDirection, hiddenDirections]);

  const statusLegendPayload = useMemo(() =>
    callsByStatus.map((d, i) => ({ value: d.name, color: PIE_COLORS[i % PIE_COLORS.length], type: 'circle', inactive: hiddenStatuses.has(d.name) })),
  [callsByStatus, hiddenStatuses]);

  const directionLegendPayload = useMemo(() =>
    callsByDirection.map((d, i) => ({ value: d.name, color: PIE_COLORS[i % PIE_COLORS.length], type: 'circle', inactive: hiddenDirections.has(d.name) })),
  [callsByDirection, hiddenDirections]);

  const toggleLineMember = useCallback((entry) => {
    setHiddenLineMembers((prev) => {
      const next = new Set(prev);
      if (next.has(entry.dataKey)) next.delete(entry.dataKey);
      else next.add(entry.dataKey);
      return next;
    });
  }, []);

  const toggleMember = useCallback((entry) => {
    setHiddenMembers((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const filteredMemberTotalsPieData = useMemo(() =>
    memberTotalsPieData.filter((d) => !hiddenMembers.has(d.name)),
  [memberTotalsPieData, hiddenMembers]);

  const memberTotalsLegendPayload = useMemo(() =>
    memberTotalsPieData.map((d, i) => ({ value: d.name, color: MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length], type: 'circle', inactive: hiddenMembers.has(d.name) })),
  [memberTotalsPieData, hiddenMembers]);

  const csvFilename = (title) => `${title}_${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.csv`;

  // --- Render ---

  if (isLoading || isFetching) {
    return (
      <div className="app-page__container">
        <div className="app-panels">
          <div className="list-panel__wrapper">
            <div className="list-panel">
              <PageLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header u-flex-direction-column">
              <div className="app-page__header__title cdr-records__title">VoIP Reports</div>
            </div>
            <div className="box responsive-table cdr-records__grid">
              <div className="cdr-records__filter-wrapper">
                <div className="cdr-records__filter-label">
                  FILTER BY:
                  {isAnyFilterSelected && (
                    <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
                  )}
                </div>
                <div className="row">
                  <div>
                    <UtilityInlineGrid>
                      <DateRangeDropdown
                        name="Reports Date Picker"
                        startDate={startDate}
                        endDate={endDate}
                        selectDate={selectDate}
                        minDate={moment().subtract(1, 'year')}
                        maxDate={moment()}
                        activeKey={activeKey}
                        position="center"
                        dropdownMenuItems={DATE_PICKER_LABELS}
                        selectDateRange={calculateDateRange}
                        dataCypress="reportsDatePicker"
                        isCustomDate
                      />
                      <Dropdown
                        position="bottom"
                        activeKey={selectedMember || ''}
                        label={memberLabel}
                        type={selectedMember ? 'primary' : 'input'}
                        outlined={!!selectedMember}
                        name="reportsMember"
                        onSelect={(ext) => setSelectedMember(ext === 'all' || ext === selectedMember ? null : ext)}
                        dataCypress="reportsMember"
                      >
                        <DropdownMenuItem
                          id="all"
                          label="All Members"
                          icon={!selectedMember ? 'checkmark' : ''}
                        />
                        {members.map((m) => (
                          <DropdownMenuItem
                            key={m.extension}
                            id={m.extension}
                            label={`${m.firstName} ${m.lastName} (ext ${m.extension})`}
                            icon={selectedMember === m.extension ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        position="bottom"
                        activeKey={selectedRingGroup || ''}
                        label={ringGroupLabel}
                        type={selectedRingGroup ? 'primary' : 'input'}
                        outlined={!!selectedRingGroup}
                        name="reportsRingGroup"
                        onSelect={(rg) => setSelectedRingGroup(rg === 'all' || rg === selectedRingGroup ? null : rg)}
                        dataCypress="reportsRingGroup"
                      >
                        <DropdownMenuItem
                          id="all"
                          label="All Ring Groups"
                          icon={!selectedRingGroup ? 'checkmark' : ''}
                        />
                        {ringGroupOptions.map((rg) => (
                          <DropdownMenuItem
                            key={rg.id}
                            id={rg.id}
                            label={rg.value}
                            icon={selectedRingGroup === rg.id ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                    </UtilityInlineGrid>
                  </div>
                </div>
              </div>

              {/* Report Tabs */}
              <div className="voip-reports__tabs">
                <div className="voip-reports__tabs-left">
                  {REPORT_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`voip-reports__tab${activeTab === tab.id ? ' voip-reports__tab--active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="voip-reports__view-toggle">
                  <button
                    type="button"
                    className={`voip-reports__view-btn${viewMode === 'graphs' ? ' voip-reports__view-btn--active' : ''}`}
                    onClick={() => setViewMode('graphs')}
                  >
                    Graphs
                  </button>
                  <button
                    type="button"
                    className={`voip-reports__view-btn${viewMode === 'table' ? ' voip-reports__view-btn--active' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    Table
                  </button>
                </div>
              </div>

              {records.length === 0 && (
                <EmptyMessage section="VoIP Reports" />
              )}

              {records.length > 0 && activeTab === 'summary' && (
                <div className="voip-reports__charts">
                  {viewMode === 'table' && (
                    <div className="voip-reports__chart-card">
                      <div className="voip-reports__table-header">
                        <h3 className="voip-reports__chart-title">Call Summary</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            className="voip-reports__export-btn"
                            onClick={() => downloadCsv(
                              [
                                { key: 'period', header: 'Period' },
                                { key: 'total', header: 'Total Calls' },
                                { key: 'avgPerDay', header: 'Avg. Calls/Day' },
                                { key: 'inbound', header: '# Inbound' },
                                { key: 'outbound', header: '# Outbound' },
                                { key: 'missed', header: '# Missed' },
                                { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` },
                                { key: 'avgHandleTime', header: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
                              ],
                              sortedSummaryTableData,
                              csvFilename('Call Summary'),
                            )}
                          >
                            Export CSV
                          </button>
                          <div className="voip-reports__grouping-selector">
                            {[
                              { id: 'total', label: 'Total' },
                              { id: 'day', label: 'By Day' },
                              { id: 'week', label: 'By Week' },
                              { id: 'month', label: 'By Month' },
                            ].map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                className={`voip-reports__grouping-btn${summaryGrouping === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
                                onClick={() => setSummaryGrouping(opt.id)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="voip-reports__table-container">
                        <table className="voip-reports__table">
                          <thead>
                            <tr>
                              {[
                                { key: 'period', label: 'Period' },
                                { key: 'total', label: 'Total Calls' },
                                { key: 'avgPerDay', label: 'Avg. Calls/Day' },
                                { key: 'inbound', label: '# Inbound' },
                                { key: 'outbound', label: '# Outbound' },
                                { key: 'missed', label: '# Missed' },
                                { key: 'pctMissed', label: '% Missed' },
                                { key: 'avgHandleTime', label: 'Avg. Handle Time' },
                              ].map((col) => (
                                <th
                                  key={col.key}
                                  className="voip-reports__th--sortable"
                                  onClick={() => handleSummarySort(col.key)}
                                >
                                  {col.label}
                                  <span className={`voip-reports__sort-arrows${summarySortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                                    <span className={summarySortColumn === col.key && summarySortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                                    <span className={summarySortColumn === col.key && !summarySortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                                  </span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedSummaryTableData.map((row) => (
                              <tr key={row.period}>
                                <td>{row.period}</td>
                                <td>{row.total}</td>
                                <td>{row.avgPerDay}</td>
                                <td>{row.inbound}</td>
                                <td>{row.outbound}</td>
                                <td>{row.missed}</td>
                                <td>{row.pctMissed}%</td>
                                <td>{formatHandleTime(row.avgHandleTime)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {viewMode === 'graphs' && (
                    <>
                      <div className="voip-reports__pie-row">
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Calls by Status</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Status' }, { key: 'value', header: 'Count' }], callsByStatus, csvFilename('Calls by Status'))}>Export CSV</button>
                          </div>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={filteredCallsByStatus}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                innerRadius={50}
                                isAnimationActive={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {filteredCallsByStatus.map((entry) => {
                                  const origIndex = callsByStatus.findIndex((d) => d.name === entry.name);
                                  return <Cell key={entry.name} fill={PIE_COLORS[origIndex % PIE_COLORS.length]} />;
                                })}
                              </Pie>
                              <Tooltip />
                              <Legend payload={statusLegendPayload} onClick={toggleStatus} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Calls by Direction</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Direction' }, { key: 'value', header: 'Count' }], callsByDirection, csvFilename('Calls by Direction'))}>Export CSV</button>
                          </div>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={filteredCallsByDirection}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                innerRadius={50}
                                isAnimationActive={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {filteredCallsByDirection.map((entry) => {
                                  const origIndex = callsByDirection.findIndex((d) => d.name === entry.name);
                                  return <Cell key={entry.name} fill={PIE_COLORS[origIndex % PIE_COLORS.length]} />;
                                })}
                              </Pie>
                              <Tooltip />
                              <Legend payload={directionLegendPayload} onClick={toggleDirection} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls Per Hour</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'hour', header: 'Hour' }, { key: 'avgCalls', header: 'Avg Calls' }], callsByHour, csvFilename('Average Calls Per Hour'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={callsByHour}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" tick={{ fontSize: 12 }} interval={1} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="avgCalls" name="Avg Calls" fill="#1976d2" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls by Day of Week</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'day', header: 'Day' }, { key: 'avgCalls', header: 'Avg Calls' }], avgCallsByDayOfWeek, csvFilename('Average Calls by Day of Week'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={avgCallsByDayOfWeek}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="avgCalls" name="Avg Calls" fill="#1976d2" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Total Calls Per Day</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'date', header: 'Date' }, { key: 'calls', header: 'Calls' }], callsPerDay, csvFilename('Total Calls Per Day'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={callsPerDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="calls" name="Calls" fill="#1976d2" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Incoming vs Outgoing Calls Per Day</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'date', header: 'Date' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], callsByDirectionPerDay, csvFilename('Incoming vs Outgoing Calls Per Day'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={callsByDirectionPerDay}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="inbound" name="Inbound" fill="#4caf50" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="outbound" name="Outbound" fill="#ff9800" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>
              )}

              {records.length > 0 && activeTab === 'members' && (
                <div className="voip-reports__charts">
                  {viewMode === 'table' && (
                  <div className="voip-reports__chart-card">
                    <div className="voip-reports__table-header">
                      <h3 className="voip-reports__chart-title">Member Breakdown</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="voip-reports__export-btn"
                          onClick={() => {
                            const cols = [
                              { key: 'name', header: 'Name' },
                              { key: 'extension', header: 'Ext' },
                              ...(memberGrouping !== 'total' ? [{ key: 'period', header: 'Period' }] : []),
                              { key: 'total', header: 'Total Calls' },
                              { key: 'avgPerDay', header: 'Avg. Calls/Day' },
                              { key: 'inbound', header: '# Inbound' },
                              { key: 'outbound', header: '# Outbound' },
                              { key: 'missed', header: '# Missed' },
                              { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` },
                              { key: 'avgHandleTime', header: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
                            ];
                            downloadCsv(cols, sortedMemberStats, csvFilename('Member Breakdown'));
                          }}
                        >
                          Export CSV
                        </button>
                        <div className="voip-reports__grouping-selector">
                          {[
                            { id: 'total', label: 'Total' },
                            { id: 'day', label: 'By Day' },
                            { id: 'week', label: 'By Week' },
                            { id: 'month', label: 'By Month' },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              className={`voip-reports__grouping-btn${memberGrouping === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
                              onClick={() => setMemberGrouping(opt.id)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="voip-reports__table-container">
                      <table className="voip-reports__table">
                        <thead>
                          <tr>
                            {[
                              { key: 'name', label: 'Name' },
                              { key: 'extension', label: 'Ext' },
                              ...(memberGrouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
                              { key: 'total', label: 'Total Calls' },
                              { key: 'avgPerDay', label: 'Avg. Calls/Day' },
                              { key: 'inbound', label: '# Inbound' },
                              { key: 'outbound', label: '# Outbound' },
                              { key: 'missed', label: '# Missed' },
                              { key: 'pctMissed', label: '% Missed' },
                              { key: 'avgHandleTime', label: 'Avg. Handle Time' },
                            ].map((col) => (
                              <th
                                key={col.key}
                                className="voip-reports__th--sortable"
                                onClick={() => handleSort(col.key)}
                              >
                                {col.label}
                                <span className={`voip-reports__sort-arrows${sortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                                  <span className={sortColumn === col.key && sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                                  <span className={sortColumn === col.key && !sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedMemberStats.map((m, idx) => (
                            <tr key={memberGrouping === 'total' ? m.extension : `${m.extension}-${m.period}-${idx}`}>
                              <td>{m.name}</td>
                              <td>{m.extension}</td>
                              {memberGrouping !== 'total' && <td>{m.period}</td>}
                              <td>{m.total}</td>
                              <td>{m.avgPerDay}</td>
                              <td>{m.inbound}</td>
                              <td>{m.outbound}</td>
                              <td>{m.missed}</td>
                              <td>{m.pctMissed}%</td>
                              <td>{formatHandleTime(m.avgHandleTime)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {viewMode === 'graphs' && (
                    <>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Total Calls Per Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'total', header: 'Total Calls' }], callsPerMember, csvFilename('Total Calls Per Member'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                          <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                            <Tooltip />
                            <Bar dataKey="total" name="Total Calls" radius={[0, 4, 4, 0]}>
                              {callsPerMember.map((entry, i) => (
                                <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Daily Calls Per Member</h3>
                          <button
                            type="button"
                            className="voip-reports__export-btn"
                            onClick={() => {
                              const cols = [{ key: 'date', header: 'Date' }];
                              memberCallsPerDay.memberKeys.forEach((ext) => {
                                const member = callsPerMember.find((m) => m.extension === ext);
                                cols.push({ key: ext, header: member ? member.name : ext });
                              });
                              downloadCsv(cols, memberCallsPerDay.data, csvFilename('Daily Calls Per Member'));
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart data={memberCallsPerDay.data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend onClick={toggleLineMember} />
                            {memberCallsPerDay.memberKeys.map((ext, i) => {
                              const member = callsPerMember.find((m) => m.extension === ext);
                              const label = member ? member.name : ext;
                              return (
                                <Line
                                  key={ext}
                                  type="monotone"
                                  dataKey={ext}
                                  name={label}
                                  stroke={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]}
                                  strokeWidth={2}
                                  dot={false}
                                  connectNulls
                                  hide={hiddenLineMembers.has(ext)}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls Per Day by Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'avgPerDay', header: 'Avg Calls/Day' }], callsPerMember, csvFilename('Average Calls Per Day by Member'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                          <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                            <Tooltip />
                            <Bar dataKey="avgPerDay" name="Avg Calls/Day" radius={[0, 4, 4, 0]}>
                              {callsPerMember.map((entry, i) => (
                                <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Inbound vs Outbound Per Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], callsPerMember, csvFilename('Inbound vs Outbound Per Member'))}>Export CSV</button>
                        </div>
                        <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                          <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="inbound" name="Inbound" fill="#4caf50" radius={[0, 4, 4, 0]} />
                            <Bar dataKey="outbound" name="Outbound" fill="#ff9800" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="voip-reports__pie-row">
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Total Call Distribution Per Member</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'value', header: 'Total Calls' }], memberTotalsPieData, csvFilename('Total Call Distribution Per Member'))}>Export CSV</button>
                          </div>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={filteredMemberTotalsPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                innerRadius={50}
                                isAnimationActive={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {filteredMemberTotalsPieData.map((entry) => {
                                  const origIndex = memberTotalsPieData.findIndex((d) => d.name === entry.name);
                                  return <Cell key={entry.name} fill={MEMBER_CHART_COLORS[origIndex % MEMBER_CHART_COLORS.length]} />;
                                })}
                              </Pie>
                              <Tooltip />
                              <Legend payload={memberTotalsLegendPayload} onClick={toggleMember} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Missed Call % by Member</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` }], callsPerMember, csvFilename('Missed Call Pct by Member'))}>Export CSV</button>
                          </div>
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                            <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip formatter={(v) => `${v}%`} />
                              <Bar dataKey="pctMissed" name="% Missed" fill="#e91e63" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RhinoVOIPReports;
